import type { ProfessionalDesignationTypeEnum } from '@/lib/pages/forms/fields';
import { getDisplayAddressFromAddress } from '@/lib/pages/forms/helpers';

import type { YourInformation } from '@/lib/heyapi/types.gen';

type YourInformationFormAllowEmpty = {
  [K in keyof YourInformation]: YourInformation[K] | '' | null;
};

export type YourInformationForm = Omit<
  YourInformationFormAllowEmpty,
  'professionalDesignation'
> & {
  professionalDesignation: Record<ProfessionalDesignationTypeEnum, boolean>;
  mailingDisplayAddress: string;
  practiceDisplayAddress: string;
  agencyName: string;
  yearMonthYear: string;
  yearMonthMonth: string;
};

export const DefaultValues: YourInformationForm = {
  agencyUuid: '',
  agencyName: '',
  associationCode: '',
  policyContractState: null,
  firstName: '',
  middleName: '',
  lastName: '',
  professionalDesignation: {
    MD: false,
    MS: false,
    BDS: false,
    DDS: false,
    DMD: false,
    MSD: false,
  },
  dateOfBirth: null,
  emailAddress: '',
  postGraduationEmail: '',
  cellPhoneNumber: '',
  mailingAddress: {
    line1: '',
    line2: '',
    city: '',
    county: '',
    zipCode: '',
    state: null,
  },
  memberOfSda: null,
  nameOfDentalSchool: '',
  yearMonthGraduation: '',
  yearMonthYear: '',
  yearMonthMonth: '',
  practiceSpecialty: null,
  primaryPracticeAddress: {
    line1: '',
    line2: '',
    city: '',
    county: '',
    zipCode: '',
    state: null,
  },
  firstTimePractice: null,
  mailingDisplayAddress: '',
  practiceDisplayAddress: '',
};

export const selectableMonth = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
  { value: '7', label: '7' },
  { value: '8', label: '8' },
  { value: '9', label: '9' },
  { value: '10', label: '10' },
  { value: '11', label: '11' },
  { value: '12', label: '12' },
];

type RequiredFields<K> = {
  [Property in keyof K]?: boolean;
};

type FormRequiredFields = RequiredFields<Required<YourInformationForm>>;

const RequiredFields: FormRequiredFields = {
  agencyUuid: true,
  agencyName: true,
  associationCode: false,
  policyContractState: true,
  firstName: true,
  middleName: false,
  lastName: true,
  professionalDesignation: true,
  dateOfBirth: true,
  emailAddress: true,
  postGraduationEmail: false,
  cellPhoneNumber: true,
  mailingAddress: true,
  memberOfSda: true,
  nameOfDentalSchool: true,
  yearMonthGraduation: false,
  yearMonthYear: true,
  yearMonthMonth: true,
  practiceSpecialty: true,
  primaryPracticeAddress: true,
  firstTimePractice: true,
  mailingDisplayAddress: false,
  practiceDisplayAddress: false,
};

export function isRequired(field: keyof FormRequiredFields): boolean {
  return RequiredFields[field] ?? false;
}

export function detailsBackToFront(
  backendDetails: YourInformation | undefined
): YourInformationForm {
  if (!backendDetails) {
    return DefaultValues;
  }

  const mailingAddress = backendDetails.mailingAddress;
  let mailingDisplayAddress = '';

  if (mailingAddress) {
    mailingDisplayAddress = getDisplayAddressFromAddress({
      address: mailingAddress,
      fallbackAddress: 'No Mailing Address',
    });
  }

  const primaryPracticeAddress = backendDetails.primaryPracticeAddress;
  let practiceDisplayAddress = '';

  if (primaryPracticeAddress) {
    practiceDisplayAddress = getDisplayAddressFromAddress({
      address: primaryPracticeAddress,
      fallbackAddress: 'No Practice Address',
    });
  }

  const defaults: YourInformationForm = {
    ...DefaultValues,
    ...(backendDetails as unknown as YourInformationForm),
    mailingDisplayAddress,
    practiceDisplayAddress,
  };

  defaults.professionalDesignation = {
    ...DefaultValues.professionalDesignation,
  };

  if (backendDetails.yearMonthGraduation) {
    defaults.yearMonthYear = String(backendDetails.yearMonthGraduation.year);
    defaults.yearMonthMonth = String(backendDetails.yearMonthGraduation.month);
  }

  if (backendDetails.professionalDesignation?.includes('MD')) {
    defaults.professionalDesignation.MD = true;
  }

  if (backendDetails.professionalDesignation?.includes('DDS')) {
    defaults.professionalDesignation.DDS = true;
  }

  if (backendDetails.professionalDesignation?.includes('MS')) {
    defaults.professionalDesignation.MS = true;
  }

  if (backendDetails.professionalDesignation?.includes('DMD')) {
    defaults.professionalDesignation.DMD = true;
  }

  if (backendDetails.professionalDesignation?.includes('BDS')) {
    defaults.professionalDesignation.BDS = true;
  }

  if (backendDetails.professionalDesignation?.includes('MSD')) {
    defaults.professionalDesignation.MSD = true;
  }

  return defaults;
}

export function detailsFrontToBack(
  frontend: YourInformationForm
): YourInformation {
  const designations: Array<ProfessionalDesignationTypeEnum> = Object.entries(
    frontend.professionalDesignation
  )
    .filter(([_k, v]) => v)
    .map(([k, _v]) => k as ProfessionalDesignationTypeEnum);

  const mutableFrontend = frontend as Record<string, unknown>;
  const defaultValues = DefaultValues as Record<string, unknown>;

  Object.keys(mutableFrontend).forEach((field) => {
    if (mutableFrontend[field] === '' && defaultValues[field] === null) {
      mutableFrontend[field] = null;
    }
  });

  let year = '';
  let month = '';

  if (frontend.yearMonthYear) {
    year = frontend.yearMonthYear;
  }

  if (frontend.yearMonthMonth) {
    month = frontend.yearMonthMonth;
  }

  const details: YourInformation = {
    ...frontend,
    professionalDesignation: designations,
    yearMonthGraduation: {
      year: Number(year),
      month: Number(month),
    },
  } as unknown as YourInformation;

  return details;
}
